import { type FC, memo, useEffect, useRef, useState } from 'react';

import { useInterstateTheme } from '@interstate/components/InterstateThemeProvider';
import { SelectInput } from '@interstate/components/SelectInput';
import { TextInput } from '@makemydeal/dr-shared-components/text-input';
import { Typography } from '@interstate/components/Typography';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';

import {
    CODE,
    TYPE,
    INCENTIVE_DUPLICATE_CODE,
    INCENTIVE_FIELD_NAMES,
    OPTIONAL_INDICATOR,
    REQUIRED_FIELD_MESSAGE
} from '../../constants';
import { ContainerGridItem, dealLockHooks, formatUtils } from '@makemydeal/dr-dash-components';
import type { ManualIncentiveEditEntry } from '@makemydeal/dr-dash-store/types/manualIncentiveTypes';
import { INCENTIVE_TYPE_VALUES, NON_MANUAL_INCENTIVE_DISPLAY_TYPES } from '@makemydeal/dr-dash-types';

import { IncentivesAmountField } from './RebatesAmountField';
import { ManualRebatesTypeDisplayContainer, TypeInputOrDisplayContainer } from './Rebates.style';
import type { RebatesTableInputProps } from '../../types';
import { useProgramsAndIncentivesContext } from '../ProgramsAndIncentivesContext';
export const RebatesTableInput: FC<RebatesTableInputProps> = memo(function RebatesTableInput({
    required,
    displayAsRequired,
    disabled,
    deleted,
    displayType,
    manual,
    incentiveLabel,
    fieldValue,
    fieldName,
    hasCodeDuplicate,
    isFromProgramIncentives,
    isRestorationEnabled,
    incentiveIndex,
    handleInputChange,
    onValidationChange
}) {
    const theme = useInterstateTheme();

    const { showConfirmationModal } = useProgramsAndIncentivesContext();

    const isLocked = dealLockHooks.useIsDealLocked();
    const isLockedOrDeleted = isLocked || deleted;
    const isNonManualDisplayType = (Object.values(NON_MANUAL_INCENTIVE_DISPLAY_TYPES) as string[]).includes(displayType);
    const isManualIncentive = isRestorationEnabled ? !isNonManualDisplayType : manual && !isFromProgramIncentives;
    const isCodeFieldDisabled = !isManualIncentive && incentiveLabel === CODE;

    const [isFieldTouched, setIsFieldTouched] = useState(false);

    const fieldPrevValue = useRef<string | number | undefined>(fieldValue);

    useEffect(() => {
        fieldPrevValue.current = fieldValue;
    }, [fieldValue]);

    const formatFieldValue = (value: string | number, fieldName: keyof ManualIncentiveEditEntry) => {
        if (fieldName === INCENTIVE_FIELD_NAMES.CODE && (value as string).trim() !== '') {
            return formatUtils.convertToNumber(value as string);
        }
        return value;
    };

    const handleBlurOrKeyDown = (value: string | number) => {
        const validateRequiredCheck = required ? Boolean(value.toString()) : true;
        const formattedValue = formatFieldValue(value, fieldName);

        if (formattedValue !== fieldPrevValue.current) {
            handleInputChange(formattedValue, fieldName, incentiveIndex);
            fieldPrevValue.current = formattedValue;
        }

        setIsFieldTouched(!validateRequiredCheck);

        // Call onValidationChange for validation tracking
        if (onValidationChange) {
            const hasError = !validateRequiredCheck;
            onValidationChange({
                incentiveIndex,
                fieldName,
                hasError
            });
        }
    };

    useEffect(() => {
        if (deleted) {
            setIsFieldTouched(false);
        }
    }, [deleted, fieldName]);

    useEffect(() => {
        if (required && showConfirmationModal && !fieldValue) {
            setIsFieldTouched(true);
        }
    }, [showConfirmationModal]);

    const getDisplayLabel = (label: string) => {
        const showAsRequired = required || displayAsRequired;
        return showAsRequired ? label : `${label} (${OPTIONAL_INDICATOR})`;
    };

    if (incentiveLabel === TYPE) {
        const selectOptions = [INCENTIVE_TYPE_VALUES.dealerCash, INCENTIVE_TYPE_VALUES.customerCash];
        const selectableValue =
            !fieldValue || selectOptions.some((option) => option.value === fieldValue)
                ? (fieldValue as string)
                : INCENTIVE_TYPE_VALUES.customerCash.value;
        const showTypeDropdown = isManualIncentive;
        const typeInputOrDisplay = showTypeDropdown ? (
            <>
                <ContainerGridItem xs={12} md="hidden">
                    <Typography variant={'label-md'} color="base.color.gray.600" sx={{ marginBottom: '6px' }}>
                        {getDisplayLabel(incentiveLabel)}
                    </Typography>
                </ContainerGridItem>

                <SelectInput
                    name={`type-${incentiveIndex}`}
                    data-testid={`type-${incentiveIndex}`}
                    options={selectOptions}
                    size={'sm'}
                    displayLabel={false}
                    errorMessage={REQUIRED_FIELD_MESSAGE}
                    hasError={isFieldTouched}
                    value={selectableValue}
                    required={required && !deleted}
                    disabled={isLockedOrDeleted}
                    displayDeselectOption={false}
                    onChange={(e) => handleBlurOrKeyDown(e.target.value as string)}
                />
            </>
        ) : (
            <ManualRebatesTypeDisplayContainer theme={theme}>
                <ContainerGridItem xs={12} md="hidden">
                    <div data-testid={`type-label-${incentiveIndex}`} className="type-display">
                        {incentiveLabel}
                    </div>
                </ContainerGridItem>

                <div className="type-value" style={{ minHeight: '2rem' }}>
                    {displayType}
                </div>
            </ManualRebatesTypeDisplayContainer>
        );
        return <TypeInputOrDisplayContainer>{typeInputOrDisplay}</TypeInputOrDisplayContainer>;
    } else {
        const errorDisplay = hasCodeDuplicate && incentiveLabel === CODE ? INCENTIVE_DUPLICATE_CODE : REQUIRED_FIELD_MESSAGE;
        const hasError = (hasCodeDuplicate && incentiveLabel === CODE) || (required && !fieldValue);

        return (
            <div className={`text-input-${fieldName}`}>
                <ContainerGridItem xs={12} md="hidden">
                    <Typography variant={'label-md'} color="base.color.gray.600" sx={{ marginBottom: '6px', whiteSpace: 'nowrap' }}>
                        {getDisplayLabel(incentiveLabel)}
                    </Typography>
                </ContainerGridItem>

                {fieldName === INCENTIVE_FIELD_NAMES.AMOUNT ? (
                    <IncentivesAmountField
                        fieldName={fieldName}
                        index={incentiveIndex}
                        errorDisplay={errorDisplay}
                        required={required}
                        deleted={isLockedOrDeleted}
                        fieldValue={fieldValue as number}
                        {...(handleBlurOrKeyDown && {
                            handleBlurOrKeyDown: (value) => handleBlurOrKeyDown(value)
                        })}
                    />
                ) : (
                    <TextInput
                        displayLabel={false}
                        data-testid={`${fieldName}-${incentiveIndex}`}
                        name={`${fieldName}-${incentiveIndex}`}
                        value={fieldValue?.toString()}
                        errorMessage={hasError ? errorDisplay : ''}
                        hasError={isFieldTouched || hasError}
                        required={required}
                        disabled={isLockedOrDeleted || isCodeFieldDisabled || disabled}
                        size={'sm'}
                        {...{
                            onBlur: (e) => handleBlurOrKeyDown(e.target.value),
                            onKeyDown: (e) => {
                                if (e.key === 'Enter') {
                                    handleBlurOrKeyDown(e.currentTarget.value);
                                }
                            }
                        }}
                        selectOnFocusSelector={featureToggleSelectors.isAutoSelectContentOnSlideOutsEnabledAfterBootstrap}
                    />
                )}
            </div>
        );
    }
});
